import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LANGS } from "@/lib/translations";

const COMPANY = "Pivot Transfer by Raphael";
const LOCATION = "Zürich, Switzerland";
const CONTACT = "legal@pivotransition.ch";

// Terms & Conditions and Terms of Use are provided in English (DE/FR on request).
const EN_DOCS = {
  terms: {
    sections: [
      { h: "1. Provider", p: `These Terms & Conditions ("Terms") govern the sale of digital products offered by ${COMPANY} ("we", "us", "our"), a business established in ${LOCATION}. By purchasing a product you agree to these Terms.` },
      { h: "2. Products", p: "We sell digital documents under the 'PivoT ransition' brand — the Guide and Workbook, each in a B2C (individual) and a B2B (business) edition, plus a B2B set — delivered electronically as downloadable, protected PDF files." },
      { h: "3. Prices & payment", p: "All prices are stated in Swiss Francs (CHF/SFr) and are inclusive of any applicable Swiss VAT where required. Indicative conversions in other currencies are provided for convenience only and are based on daily foreign-exchange rates; the amount charged is the SFr price. Payments are processed securely by Stripe; we do not store your card details." },
      { h: "4. Delivery & licence", p: "Access to the download is granted immediately after your payment is confirmed. Files are personalised (watermarked) and protected. You receive a personal, non-exclusive, non-transferable licence for your own use; resale, redistribution or sharing is not permitted." },
      { h: "5. Right of withdrawal", p: "Because our products are digital content delivered immediately, you expressly agree that delivery begins at purchase and, to the extent permitted by law, you waive any right of withdrawal once the download has been made available. Statutory rights that cannot be waived remain unaffected." },
      { h: "6. Liability", p: "The guides provide general educational information and do not constitute professional, career, legal or financial advice. To the extent permitted by Swiss law, our liability is limited to intent and gross negligence." },
      { h: "7. Governing law & jurisdiction", p: `These Terms are governed by the substantive laws of Switzerland, excluding conflict-of-law rules and the CISG. The exclusive place of jurisdiction is ${LOCATION}, subject to any mandatory statutory places of jurisdiction.` },
      { h: "8. Contact", p: `${COMPANY} · ${LOCATION} · ${CONTACT}` },
    ],
  },
  use: {
    sections: [
      { h: "1. Acceptance", p: `By accessing and using this website operated by ${COMPANY} (${LOCATION}), you agree to these Terms of Use. If you do not agree, please do not use the website.` },
      { h: "2. Permitted use", p: "You may use the website for lawful, personal and informational purposes only. You agree not to interfere with its operation or security, attempt unauthorised access, or use automated means to scrape or overload the service." },
      { h: "3. Intellectual property", p: "All content on this website — including text, design, graphics, the hero artwork and the guides — is owned by or licensed to us and protected by intellectual-property laws. You may not copy, reproduce or distribute it without our prior written consent." },
      { h: "4. No warranty", p: "The website and its content are provided 'as is' and 'as available'. We make no warranty that the website will be uninterrupted, error-free or free of harmful components." },
      { h: "5. External links", p: "The website may contain links to third-party sites (such as Stripe). We are not responsible for the content or practices of those third parties." },
      { h: "6. Changes", p: "We may update these Terms of Use and the website at any time. The version published at the time of your visit applies." },
      { h: "7. Governing law", p: `These Terms of Use are governed by Swiss law, with exclusive jurisdiction in ${LOCATION}, subject to mandatory statutory provisions.` },
      { h: "8. Contact", p: `${COMPANY} · ${LOCATION} · ${CONTACT}` },
    ],
  },
};

export default function Legal({ doc }) {
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);

  const isPrivacy = doc === "privacy";
  const title = t.legalUI.docs[doc];
  const label = isPrivacy ? t.privacy.label : "Legal";
  const sections = isPrivacy ? t.privacy.sections : EN_DOCS[doc].sections;
  const showEnglishNote = !isPrivacy && lang !== "en";

  return (
    <div className="relative bg-[#0A0A0C] text-white min-h-screen">
      <div className="border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            data-testid="legal-back"
            to="/"
            className="flex items-center gap-2 label-caps text-white/60 hover:text-white transition-colors duration-300"
          >
            <ArrowLeft size={16} /> {t.legalUI.back}
          </Link>
          <div className="inline-flex items-center gap-1 rounded-full border border-white/15 p-1" data-testid="legal-lang-switcher">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`legal-lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`label-caps px-2.5 py-1 rounded-full transition-colors duration-300 ${
                  lang === l.code ? "bg-white text-black" : "text-white/60 hover:text-white"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-20 lg:py-28" data-testid={`legal-${doc}`}>
        <p className="label-caps text-[var(--neon-cyan)] mb-5">{label}</p>
        <h1 className="font-origin font-light text-4xl sm:text-5xl tracking-tight">{title}</h1>
        <p className="mt-4 font-mono2 text-xs text-white/40">{t.legalUI.updated}</p>

        {showEnglishNote && (
          <p className="mt-8 text-sm text-white/50 border border-white/10 rounded-xl p-4 bg-white/[0.03]" data-testid="legal-lang-note">
            {t.legalUI.available}
          </p>
        )}

        <div className="mt-14 space-y-12">
          {sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-future text-lg mb-3">{s.h}</h2>
              <p className="text-white/65 leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 flex flex-wrap gap-6 label-caps text-white/40">
          <Link to="/terms" className="hover:text-white transition-colors duration-300">{t.legalUI.docs.terms}</Link>
          <Link to="/privacy" className="hover:text-white transition-colors duration-300">{t.legalUI.docs.privacy}</Link>
          <Link to="/terms-of-use" className="hover:text-white transition-colors duration-300">{t.legalUI.docs.use}</Link>
        </div>
      </article>
    </div>
  );
}
